import { memo, useEffect, useState } from 'react'

import { PublisherResponse, TogglePublisherRequest } from '@shared/models'

import { Tokens } from '@/assets/tokens'

import { Alert, Api, Confirmation } from '@/modules'

import { Button, Divider, Flex, Switch } from '@/components'
import { ColumnType, ContentFilter, Table } from '@/components/derived'

import { useRouting } from '@/hooks'
import { useObserver } from '@/modules/observer'
import { getFromToElements } from '@/utils/pagination'
import { useCompanyContext } from '../CompanyDetails.context'

export const PublishersTable = memo(Component)

export type PublisherFilters = {
  name?: string
  page?: number | string
  size?: number
  onlyActive?: boolean
}

const COMPANY_PUBLISHER_TABLE = 'company-publisher-table'

function Component(): JSX.Element | null {
  const { currentPage, companyID, changeQueryParam } = useRouting()
  const { isLoadingPublishers, publishers, reloadPublishers } = useCompanyContext()

  const [publisherFilter, setPublisherFilter] = useState<PublisherFilters>({
    page: currentPage,
    onlyActive: true,
  })
  const [onlyActive, setOnlyActive] = useState(true)

  const { sendMessage } = useObserver()

  const { sendAlert } = Alert.Hook()
  const { open: openConfirmation } = Confirmation.Hook({
    text: 'Tem certeza que deseja desativar este publicador?',
    confirmText: 'Sim, desativar',
  })

  const { run: togglePublisher } = Api.Command<void>(
    Api.Methods.PATCH,
    'companies/:companyID/publishers/:publisherID/toggle',
  )

  useEffect(() => {
    reloadPublishers(publisherFilter)
  }, [publisherFilter])

  useEffect(() => {
    setPublisherFilter({ name: publisherFilter?.name, page: currentPage, onlyActive })
  }, [currentPage, onlyActive])

  function handleToggleActiveFilter(checked: boolean): void {
    setOnlyActive(checked)
    const page = Number(currentPage) > 1 ? '1' : currentPage
    changePage(page)
  }

  function handleEditPublisher(publisherId: string) {
    changeQueryParam({
      param: 'editPublisherId',
      value: publisherId,
    })
  }

  function changePage(page: string): void {
    sendMessage(COMPANY_PUBLISHER_TABLE, page)
  }

  function onApplyFilters(channel?: string): void {
    const page = Number(currentPage) > 1 ? '1' : currentPage
    setPublisherFilter({ name: channel, page: page, onlyActive })
    changePage(page)
  }

  function adjustPageIfNeeded(totalAfterAction: number, pageSize: number = 10): void {
    const currentPageNum = Number(currentPage)
    const totalPages = Math.ceil(totalAfterAction / pageSize)

    if (currentPageNum > totalPages && totalPages > 0) {
      changePage(String(totalPages))
    }
  }

  function handleToggle(publisher: PublisherResponse, newState: boolean): void {
    const executeToggle = () => {
      togglePublisher<TogglePublisherRequest>({
        binds: [
          ['companyID', companyID!],
          ['publisherID', publisher.id],
        ],
        input: { isActive: newState },
        onSuccess: () => {
          sendAlert({
            type: 'success',
            title: newState ? 'Publicador ativado' : 'Publicador desativado',
            message: `Publicador ${newState ? 'ativado' : 'desativado'} com sucesso`,
          })

          const willRemoveFromList = (onlyActive && !newState) || (!onlyActive && newState)
          if (willRemoveFromList && publishers?.total) {
            adjustPageIfNeeded(publishers.total - 1)
          }

          reloadPublishers(publisherFilter)
        },
      })
    }

    if (!newState) {
      openConfirmation(executeToggle)
    } else {
      executeToggle()
    }
  }

  const columns: ColumnType<PublisherResponse>[] = [
    {
      key: 'name',
      title: 'Nome',
    },
    {
      key: 'weight',
      title: 'Peso',
      width: 80,
      render: (_, item) => item.weight ?? '--',
    },
    {
      key: 'minFollowers',
      title: 'Min.',
      width: 80,
      render: (_, item) => item.minFollowers ?? '--',
    },
    {
      key: 'maxFollowers',
      title: 'Max.',
      width: 80,
      render: (_, item) => item.maxFollowers ?? '--',
    },
    {
      key: 'options',
      title: 'Opções',
      width: 50,
      align: 'center',
      render: (_, item) => {
        const publisher = publishers?.elements.find((p) => p.id === item.id)
        const isDefault = item.name === 'DEFAULT'
        return (
          <Flex $type="row" $justify="flex-start" $align="stretch">
            <Button.Root
              $variant="link"
              style={{ color: Tokens.ColorUIPrimary }}
              onClick={() => handleEditPublisher(item.id)}
              disabled={!publisher?.isActive || isDefault}>
              Editar
            </Button.Root>
          </Flex>
        )
      },
    },
    {
      key: 'active',
      title: 'Ativo',
      align: 'center',
      width: 50,
      render: (_, item) => {
        const publisher = publishers?.elements.find((p) => p.id === item.id)
        const isDefault = item.name === 'DEFAULT'
        const handleSwitchToggle = () => {
          if (publisher && !isDefault) {
            const newState = !publisher.isActive
            handleToggle(publisher, newState)
          }
        }
        return <Switch.Root isChecked={publisher?.isActive ?? true} toggle={handleSwitchToggle} disabled={isDefault} />
      },
    },
  ]

  return (
    <>
      <Divider.Root>
        <Divider.Title value="Publicadores registrados" />
        <Divider.Spacer />
        <Divider.Subtitle
          value={`${getFromToElements(currentPage, publishers?.elements.length ?? 0)}/${publishers?.total ?? 0} Entrada(s)`}
        />
      </Divider.Root>

      <Divider.Root>
        <ContentFilter
          isLoading={isLoadingPublishers}
          onApply={onApplyFilters}
          onReset={onApplyFilters}
          placeholder={'Publicador'}>
          <Flex $type="row" $align="center" $gap={8}>
            <Switch.Root
              isChecked={onlyActive}
              toggle={() => handleToggleActiveFilter(!onlyActive)}
              label="Apenas ativos"
              direction="row"
            />
          </Flex>
        </ContentFilter>
      </Divider.Root>

      <Table
        isLoading={isLoadingPublishers}
        columns={columns}
        data={publishers?.elements}
        totalPages={publishers?.pages}
      />
    </>
  )
}
